import { useMemo } from "react";

import { startCase } from "lodash-es";

import { FetchOrderIndexQueryOption } from "../../../api/fulfillment/order";
import Organization from "../../../lib/organization";
import { Facility } from "api/common/facility";

export enum StockStatus {
  in_stock = "in_stock",
  partially_in_stock = "partially_in_stock",
  out_of_stock = "out_of_stock",
  unknown_stock = "unknown_stock",
}

export const ORDER_STATUS = {
  active: "Active",
  cancelled: "Cancelled",
};

export const PAGE_LINKS = {
  createOrder: "/orders/create",
  uploadCsv: "/orders/create-via-csv",
  reCreateOrder: "/orders/create?isAutoCreateShipment=true",
  syncShopifyOrder: "/sync-order-with-shopify",
  organizationDetails: "",
};

export const getShipmentTagOptions = (orgID?: string) => {
  let tagOptions = [
    {
      key: "unfulfilled_items",
      label: "Unfulfilled",
      value: "unfulfilled_items",
    },
    {
      key: "ready_to_fulfill",
      label: "Ready to Fulfill",
      value: "ready_to_fulfill",
    },
    {
      key: "customer_booking",
      label: "Customer Booking",
      value: "customer_booking",
    },
    {
      key: "processing_items",
      label: "Processing Delivery",
      value: "processing_items",
    },
    { key: "items_fulfilled", label: "Fulfilled", value: "items_fulfilled" },
    {
      key: "processing_return_items",
      label: "Processing Return",
      value: "processing_return_items",
    },
    { key: "items_returned", label: "Returned", value: "items_returned" },
    { key: "returned_to_sender", label: "Returned To Sender", value: "returned_to_sender" },
    {
      key: "pending_inventory_receiving",
      label: "Pending Inventory Receiving",
      value: "pending_inventory_receiving",
    },
    {
      key: "pending_return_to_sender",
      label: "Pending Return to Sender",
      value: "pending_inventory_receiving",
    },
  ];

  if (orgID && Organization.isIkeaOrgID(orgID)) {
    tagOptions = [
      ...tagOptions,
      {
        key: "pending_customer_pickup",
        label: "Pending Customer Pickup",
        value: "pending_customer_pickup",
      },
      {
        key: "items_picked_up_by_customer",
        label: "Items Picked Up By Customer",
        value: "items_picked_up_by_customer",
      },
    ];
  }
  return tagOptions;
};

export const getOrderStatusOptions = () => Object.values(ORDER_STATUS).map((s) => ({ key: s, value: s, label: s }));

export const getStockStatusOptions = () =>
  Object.values(StockStatus).map((status) => ({
    key: status,
    label: startCase(status),
    value: status,
  }));

export const getFacililtyOptions = (facilities: Facility[]) =>
  facilities
    ?.sort((a, b) =>
      a.name.toLowerCase() < b.name.toLowerCase() ? -1 : a.name.toLowerCase() > b.name.toLowerCase() ? 1 : 0
    )
    .map((f) => ({ key: f.name, label: `${f.address.city} (${f.name.toUpperCase()})`, value: f.name })) ?? [];

// todo type this params (move to hooks folder?)
export const useOrderIndexQueryOptions = ({
  urlState = {},
  sort = {},
  orgID = "",
  userID = "",
}: Record<any, any>): FetchOrderIndexQueryOption => {
  return useMemo(() => {
    const {
      page,
      pageSize,
      search,
      shipmentTags,
      orderStatus,
      stockStatus,
      dateFrom,
      dateTo,
      organizations,
      errorFilter,
      salesChannel,
      fulfilledFrom,
    } = urlState;
    const { field, direction } = sort;
    const isActiveAndCancelled = [ORDER_STATUS.active, ORDER_STATUS.cancelled].every((s: string) =>
      orderStatus?.includes(s)
    );
    const channels = (salesChannel as string[]).filter((s) => s !== "-");
    const showUnassignedChannel = (salesChannel as string[]).includes("-");
    return {
      page: page,
      per_page: pageSize,
      // optional query params
      ...(orgID && { organization_id: orgID }),
      ...(search && { q: String(search).trim() }),
      ...(shipmentTags.length > 0 && {
        shipment_tags_values: shipmentTags,
        shipment_tags_condition: "any",
      }),
      ...(orderStatus?.length && {
        ...(!isActiveAndCancelled && {
          cancelled: orderStatus?.includes(ORDER_STATUS.cancelled),
        }),
      }),
      ...(errorFilter.length && {
        has_errors: errorFilter.includes("urgent_errors"),
        ...(errorFilter.some((e: string) => e === "assigned_to_me" || e === "unassigned") && {
          assigned_user_id: errorFilter.includes("assigned_to_me") ? userID : "",
        }),
      }),
      ...(stockStatus?.length && { stock_status: stockStatus }),
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
      ...(organizations?.length && { organization_names: organizations }),
      ...(field && direction && { field, direction }),
      ...(channels?.length && { sales_channel: channels }),
      ...(showUnassignedChannel && { sales_channel_unassigned: true }),
      ...(fulfilledFrom && { facility: fulfilledFrom }),
    };
  }, [urlState, sort, orgID, userID]);
};
